import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, icons } from "../../../Styles";
import { ProgressCircle, Last10DaysViewer, UNIT, FeedCurveWithLabels} from "../../";
import { Graph_QL, useFeedCurves, Location, MaintainAnimal, System, Consumption, useTerminals } from "../../../Services";
import { useAppSelector } from "../../../Store";
import PenList, { ListTypes } from "../../BaseComponents/Animals/penList";
import { selectFeedCurveById } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice";
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/root";
import { useNavigate } from "react-router";
import MotorSpecs from "../../motorSpecs";
import { NewButtonList } from "../../BaseComponents/newButton";

interface penModal_Props {
  activePen?: Location;
  system: System
}
const RemainingToday = memo(({animalConsumption}: {animalConsumption?: Consumption[]}) => {
  const { t } = useTranslation();
  const consumptionToday = animalConsumption ? animalConsumption.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 0):undefined;
  const consumptionTotalToday = +((consumptionToday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedToday = +(consumptionTotalToday - (consumptionToday?.progressed ?? 0) / 1000).toFixed(2);

  return (
    <ProgressCircle
      animation
      header={t("feed.general.remainingToday", {
        defaultValue: "remaining today",
      })}
      total={consumptionTotalToday}
      progressed={consumptionProgressedToday}
      unit={<UNIT content={"KG"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>}
    />
  );
})

// ProgressCircle: Remaining Today
const RemainingYesterday = memo(({animalConsumption}: {animalConsumption?: Consumption[]}) => {
  const { t } = useTranslation();
  const consumptionYesterday = animalConsumption ? animalConsumption.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 1):undefined;
  const consumptionTotalYesterday = +((consumptionYesterday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedYesterday = +(consumptionTotalYesterday - (consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2);

  return (
    <ProgressCircle
      animation
      header={t("feed.general.remainingYesterday", {
        defaultValue: "remaining yesterday",
      })}
      total={consumptionTotalYesterday}
      progressed={consumptionProgressedYesterday}
      unit={<UNIT content={"KG"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>}
    />
  );
})

/**
 * PenModal
 * @param activePenId The pen number.
 * @returns A "div" to place in a modal to show all content from a Location Feeder Pen.
 */
export default function PenModal({ system, activePen }: penModal_Props) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const [currentPenState, ] = useState<"EDIT" | "BASIC">("BASIC");
  const [currentFeedview, setCurrentFeedview] = useState<"LAST10DAYS" | "FEEDCURVE">("FEEDCURVE");
  const dirtRef = useRef<MaintainAnimal[]>([]);
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
    const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const { call: getFeedCurves } = useFeedCurves(connection);
  const { call: getTerminals, data: terminals, updateTerminal } = useTerminals(connection)  

  useEffect(() => {
    getFeedCurves({ fetchAmount: 50, ifFeedCurveBaseInfoOnly: false })
    getTerminals({ })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const terminalsForPen = useMemo(() => terminals[domain]?.data?.filter(
      terminal => terminal.fk_locations === activePen?.location_id
  ), [terminals, domain, activePen?.location_id]);

  console.log("terminalsInfo", terminalsForPen)

  const activeAnimal = useMemo(() => ({
    animal: activePen?.animals?.[0],
  }), [activePen]);

  const feedCurve = useAppSelector((state) =>  {if (activeAnimal.animal?.feedcurve_id === undefined) { return }; return selectFeedCurveById(state.graphQlData.feedCurves, activeAnimal.animal?.feedcurve_id)})

  // FeedCurve
  const FeedCurve: JSX.Element = useMemo(() => {

    if (!feedCurve) return (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
        {t("animalPages.animal.noFeedCurve", {defaultValue: "No Feed Curve Found"})}
      </p>
    )
    
    return (
      <FeedCurveWithLabels data={feedCurve} />
    )
  }, [feedCurve, t])

  // Last10Days
  const Last10Days: JSX.Element = useMemo(() => {
    const last10Days = activeAnimal.animal?.lastTenDay

    if (!last10Days) return (
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
        {t("animalPages.animal.noFeedCurve", {defaultValue: "No Feed Curve Found"})}
      </p>
    )

    return (
      <Last10DaysViewer data={last10Days} />
    )
  }, [activeAnimal.animal?.lastTenDay, t])

  // #region | Pen State Router
  // const PenState_Router = () => {
  //   const animalsData = dirtRef.current.filter(e => e.animal_id && activeAnimal.animal?.animal_id)
  //   switch (currentPenState) {
  //     case "EDIT":
  //       return (
  //         <PEN_EDIT
  //           animalsData={animalsData}
  //           changeData={()=>{}} //TODO: actually make change function
  //           pen={activePen}
  //           penType={penTypes.simple}
  //           borderRadius={{ borderRadius: "0px 0px 10px 10px" }}
  //         />
  //       );
  //     case "BASIC":
  //       return (
  //         <PEN_BASIC
  //           pen={activePen}
  //           penType={penTypes.simple}
  //           borderRadius={{ borderRadius: "0px 0px 10px 10px" }}
  //         />
  //       )
  //     default:
  //       return null;
  //   }
  // };
  // #endregion

  // #region | Feed View State Router
  const FeedviewStateRouter = () => {
    switch (currentFeedview) {
      case "LAST10DAYS":
        return (
            <>{Last10Days}</>
        );
      case "FEEDCURVE":
        return (
            <>{FeedCurve}</>
        );
      default:
        return null;
    }
  }
  // #endregion

  // Seperator Line
  const seperatorLine = (
    <div style={{ width: "735px", display: "flex", alignItems: "center", justifyContent: "center", margin: "20px auto"}}>
      <div style={{ width: "600px", height: "2px", backgroundColor: colors.greyTertiary}}/>
    </div>
  );
  // #endregion

  return ( <>
    <div style={{ ...css.modalTop}}>
      {/* Activator, Feeder, Motor */}
      {/* <StatusDisplayList OBS: MOTOR mA NOT WORKING ATM
        headers={[t("attentions.attention"), t("locationFeeder.penPopup.feeder"), t("locationFeeder.penPopup.motor")]} 
        values={["• " + attention ,"• " + feeder, motor + " mA"]}              
        icons={[glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"}), glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"}), glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"})]}
        valueColor={[ _attentionColor(attentionColor), _feederColor(feederColor), colors.greyDarkPrimary]}
      /> */}
      
      <MotorSpecs terminals={terminalsForPen} />

      <div style={{display: "flex", gap: "30px"}}>
        <RemainingToday animalConsumption={activeAnimal.animal?.consumptions}/>
        <RemainingYesterday animalConsumption={activeAnimal.animal?.consumptions} />
      </div>
      

    </div>
    {seperatorLine}

    {/* Activations, Last Activation OBS: NOT WORKING ATM */}
    {/* {hasActivator? <>
      <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "40px"}}>
        <StatusDisplay
          header={t("locationFeeder.penPopup.activator")} 
          value={"XXXXXX"}              
          // icon={icons.activatorLF}
          icon={glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"})}
          valueColor={colors.greyDarkPrimary}
        />
        < Activations penId={activePenId ?? 0} showActivations showLastActivated/> 
      </div> 
      {seperatorLine}
    </> : <></>} */}

    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center"}}>
      {activeAnimal.animal ? ( <>
        
        {/* In this Pen */}
        <div style={{ display: "flex", gap: "10px" }}>
          <img src={icons.animalsColor} style={{width: "45px", height: "45px"}} alt={""}/>
          <div style={{display: "flex", flexDirection: "column", alignItems: "start", justifyContent: "center", gap: "2px"}}>
            <p style={{color: colors.greyPrimary, ...fonts.fontSize4, ...fonts.header}}>{t("locationFeederPages.sections.inThisPen", {defaultValue: "In this Pen"})}</p>
            <p style={{color: colors.greyDarkPrimary, ...fonts.fontSize3, ...fonts.header}}>{`${t("animalPages.animal.animal", {defaultValue: "Animal"})} ${activeAnimal.animal.animal_number}`}</p>
          </div>
        </div>

        {/* Pen/PenEdit based on PenMode */}
        <div style={{marginBottom: "20px"}}>
          {activePen ?
            <PenList
              key={activePen.location_id}
              listType={ListTypes.animals}
              headerPen={true} systemType={system.fk_system_types}
              pens={[activePen]}
              onAnimalClick={(animalId: number) => RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animal, replace: true, navArgs: { selected: animalId }, currentDomain: farm })}
              connect={{ connection: connection, farm: farm, domain: domain }}
            />
            : <></>}
        </div>

        {/* FEED VIEW */}
        <NewButtonList
          // hasActiveLoad
          contents={[
            t("animalPages.animal.feedCurve", {defaultValue: "Feed Curve"}),
            t("feed.general.feedLastDays", { defaultValue: "last 10 days" }),
          ]}
          onClick={(i) => {
            if (i === 0) {setCurrentFeedview("FEEDCURVE")}
            if (i === 1) {setCurrentFeedview("LAST10DAYS")}
          }}
          overrides={ (index) => ({
            baseStyles: {
              width: "fit-content",
              backgroundColor: (currentFeedview === "FEEDCURVE" && index === 0) || (currentFeedview === "LAST10DAYS" && index === 1) ? colors.blackPrimary : colors.greyDarkPrimary
            }
          })}
        />
        <div style={{marginTop: "10px"}}>{FeedviewStateRouter()}</div>
      </> )
        
      // IF NO ANIMAL
      : (
        <p style={{ ...css.emptyPen, opacity: "0.5", marginBottom: "10px"}}>
          {t("locationFeederPages.sections.emptyPen", {defaultValue: "Empty pen"})}
        </p>
      )}
    </div>
  </> );
}
